"use client";

import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useContributorRoles } from "@/hooks/use-app-config";

interface RoleAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
}

export function RoleAutocomplete({
  value,
  onChange,
  placeholder = "e.g. Producer, Vocalist",
  className,
  id,
}: RoleAutocompleteProps) {
  const { items: roles } = useContributorRoles();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Show ALL roles (the dropdown scrolls) — previously capped at 12/10, which
  // hid most roles so they couldn't be seen/picked.
  const filtered = value.trim().length === 0
    ? roles
    : roles.filter(r =>
        r.toLowerCase().includes(value.toLowerCase()) &&
        r.toLowerCase() !== value.toLowerCase()
      );

  const showDropdown = isOpen && filtered.length > 0;

  const handleSelect = (role: string) => {
    onChange(role);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === "ArrowDown") { setIsOpen(true); return; }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(p => (p < filtered.length - 1 ? p + 1 : p));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(p => (p > 0 ? p - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && filtered[selectedIndex]) {
          handleSelect(filtered[selectedIndex]);
        } else {
          setIsOpen(false);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { setSelectedIndex(-1); }, [value]);

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        id={id}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={e => { onChange(e.target.value); setIsOpen(true); }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        className={cn("pr-8", className)}
        autoComplete="off"
      />
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border border-border rounded-md shadow-lg max-h-52 overflow-y-auto"
        >
          <div className="p-1">
            <p className="px-2 py-1 text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
              {value.trim() ? "Matching roles" : "All roles"}
            </p>
            {filtered.map((role, i) => (
              <button
                key={role}
                type="button"
                onMouseDown={e => { e.preventDefault(); handleSelect(role); }}
                className={cn(
                  "w-full px-3 py-1.5 text-left text-sm rounded transition-colors",
                  i === selectedIndex ? "bg-foreground text-background" : "hover:bg-muted text-foreground"
                )}
              >
                {role}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
